import React, { useState } from 'react'
import { Box, IconButton, Tooltip, Menu, MenuItem, Typography, FormControl, Select } from '@mui/material'
import ViewComfyIcon from '@mui/icons-material/ViewComfy'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import SettingsIcon from '@mui/icons-material/Settings'
import { layout } from 'amhm-vc-ui-kit'
import AgoraVideoComponent from 'src/consumer/components/PublicLiveSessionPage/components/AgoraVideoComponent'
import CameraPreview from '../JoinSession/CameraPreview'
import { LSButton } from '../../LiveSessionPageStyles'
import { SessionSupportDock, useSessionSupportShell } from 'src/live'
import 'src/consumer/components/LiveSessionPage/VideoCallEnhancedStyles.css'

const VideoCallContainer = ({
  userRole,
  joinAgoraCall,
  agoraRtcProps,
  agoraRtmProps,
  onCameraClose,
  onCameraOpen,
  onAudioClose,
  onAudioOpen,
  handleLeaveRoom,
  handleEndVideoCall,
  showRejoin = false,
  screenWidth,
  menuItemsList,
  selectedMenuItem,
  setSelectedMenuItem,
  renderWhatINeed,
  drawerForMoreOptionsOpenMobile,
  setDrawerForMoreOptionsOpenMobile,
  chatProps,
  joinAgoraCallFunction,
  hasTopTabs = false,
  // New props for waiting drawer
  bookedEventInfo,
  peerChatName,
  peerChatImage,
  changeLayoutFunction,
  // Device permissions props for camera preview
  selectedCamera,
  selectedMicrophone,
  userHasAllowedAudio,
  userHasAllowedVideo,
  microphoneError,
  cameraError,
  setUserHasAllowedAudio,
  setUserHasAllowedVideo,
  setGiveCameraPermissionDialogOpen,
  setGiveMicrophonePermissionDialogOpen,
  meetingSrc,
  // Available devices for settings dropdown
  availableCameras,
  availableMicrophones,
  setSelectedCamera,
  setSelectedMicrophone,
  // Support QR code from API (preferred over clinic brand)
  supportQrCodeMediaIdFromApi,
}) => {
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null)
  const settingsMenuOpen = Boolean(settingsAnchorEl)

  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget)
  }

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null)
  }

  // Show waiting drawer for patients if they're waiting
  const isPatient = userRole === 'consumer'
  const sessionSupportShell = useSessionSupportShell({
    enabled: isPatient,
    inCall: Boolean(joinAgoraCall),
    isPatient,
    localUid: agoraRtcProps?.uid,
    bookedEventId: bookedEventInfo?.id,
    clinicBrandId: bookedEventInfo?.clinicBrandId,
    supportQrCodeMediaId: supportQrCodeMediaIdFromApi,
    sessionStartTime: bookedEventInfo?.startTime,
    fetchEngagementConfig: true,
  })

  // With the new drawer system, video call always takes full width
  // Secondary content is handled by the SessionDrawer component
  
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        maxHeight: '100%', // Ensure it doesn't exceed parent
        width: '100%',
        overflow: 'hidden', // Prevent scrolling - controls use absolute positioning
        position: 'relative',
        // Modern gradient background for the container
        background: 'linear-gradient(135deg, rgba(248, 250, 252, 1) 0%, rgba(243, 244, 255, 1) 100%)',
        '@media print': {
          height: '100% !important',
        },
      }}
      className="full-width-child-resizable-box-print-only video-call-container"
    >
      {/* Video Call Area - Full width, secondary content handled by SessionDrawer */}
      {userRole === 'mentor' && !joinAgoraCall ? (
        <div
          className="d-flex flex-column align-items-center justify-content-center h-100 w-100 hide-on-print-only"
          style={{ 
            background: 'linear-gradient(135deg, rgba(248, 250, 252, 1) 0%, rgba(243, 244, 255, 1) 100%)',
            borderRadius: '0',
            gap: '24px',
            padding: '24px',
          }}
        >
          {/* Camera Preview - Reduced size for better Join button prominence */}
          {meetingSrc === 'aumhum' && (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '320px', md: '360px' },
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <CameraPreview
                selectedCamera={selectedCamera}
                selectedMicrophone={selectedMicrophone}
                availableCameras={availableCameras}
                availableMicrophones={availableMicrophones}
                setSelectedCamera={setSelectedCamera}
                setSelectedMicrophone={setSelectedMicrophone}
                userHasAllowedVideo={userHasAllowedVideo}
                userHasAllowedAudio={userHasAllowedAudio}
                isVideoCallScreen={meetingSrc === 'aumhum'}
                meetingSrc={meetingSrc}
                microphoneError={microphoneError}
                cameraError={cameraError}
                setUserHasAllowedAudio={setUserHasAllowedAudio}
                setUserHasAllowedVideo={setUserHasAllowedVideo}
                setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
              />
            </Box>
          )}
          
          {/* Join Button and Settings */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <LSButton 
              onClick={joinAgoraCallFunction}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #4f46e5 100%)',
                border: 'none',
                color: 'white',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3), 0 4px 12px rgba(99, 102, 241, 0.2)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)'
                e.target.style.boxShadow = '0 12px 32px rgba(139, 92, 246, 0.4), 0 6px 16px rgba(99, 102, 241, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)'
                e.target.style.boxShadow = '0 8px 24px rgba(139, 92, 246, 0.3), 0 4px 12px rgba(99, 102, 241, 0.2)'
              }}
            >
              {showRejoin ? 'Rejoin Video Call' : 'Join Video Call'}
            </LSButton>
            
            {/* Settings Button */}
            <Tooltip title="Device Settings">
              <IconButton
                onClick={handleSettingsClick}
                sx={{
                  color: '#6366f1',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.2)',
                    borderColor: 'rgba(99, 102, 241, 0.4)',
                  },
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Settings Menu */}
          <Menu
            anchorEl={settingsAnchorEl}
            open={settingsMenuOpen}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 240,
                maxWidth: 280,
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                overflow: 'hidden',
              },
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 1.5, 
                  color: '#6366f1',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                }}
              >
                Device Settings
              </Typography>
              
              {/* Camera Selection */}
              {availableCameras && availableCameras.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#6b7280', 
                      mb: 0.75, 
                      display: 'block', 
                      fontWeight: 500,
                      fontSize: '11px',
                    }}
                  >
                    Camera
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ m: 0 }}>
                    <Select
                      value={selectedCamera || ''}
                      onChange={(e) => {
                        setSelectedCamera(e.target.value)
                      }}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Select camera' }}
                      sx={{
                        height: '32px',
                        fontSize: '13px',
                        '& .MuiSelect-select': {
                          py: 0.75,
                          px: 1.25,
                        },
                      }}
                    >
                      {availableCameras.map((option) => (
                        <MenuItem 
                          key={option?.deviceId} 
                          value={option?.deviceId}
                          sx={{
                            fontSize: '13px',
                            py: 0.75,
                            minHeight: '36px',
                          }}
                        >
                          {option?.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              {/* Microphone Selection */}
              {availableMicrophones && availableMicrophones.length > 0 && (
                <Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#6b7280', 
                      mb: 0.75, 
                      display: 'block', 
                      fontWeight: 500,
                      fontSize: '11px',
                    }}
                  >
                    Microphone
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ m: 0 }}>
                    <Select
                      value={selectedMicrophone || ''}
                      onChange={(e) => {
                        setSelectedMicrophone(e.target.value)
                      }}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Select microphone' }}
                      sx={{
                        height: '32px',
                        fontSize: '13px',
                        '& .MuiSelect-select': {
                          py: 0.75,
                          px: 1.25,
                        },
                      }}
                    >
                      {availableMicrophones.map((option) => (
                        <MenuItem 
                          key={option?.deviceId} 
                          value={option?.deviceId}
                          sx={{
                            fontSize: '13px',
                            py: 0.75,
                            minHeight: '36px',
                          }}
                        >
                          {option?.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              {(!availableCameras || availableCameras.length === 0) && 
               (!availableMicrophones || availableMicrophones.length === 0) && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#9ca3af', 
                    fontStyle: 'italic',
                    fontSize: '11px',
                    display: 'block',
                    textAlign: 'center',
                    py: 1,
                  }}
                >
                  No devices available
                </Typography>
              )}
            </Box>
          </Menu>
        </div>
      ) : (
        <>
          <AgoraVideoComponent
            agoraRtcProps={agoraRtcProps}
            agoraRtmProps={agoraRtmProps}
            handleLeaveRoom={handleEndVideoCall || handleLeaveRoom}
            onCameraClose={onCameraClose}
            onCameraOpen={onCameraOpen}
            onAudioClose={onAudioClose}
            onAudioOpen={onAudioOpen}
            onUserJoin={sessionSupportShell.handleRemoteUserJoin}
            onUserLeave={sessionSupportShell.handleRemoteUserLeave}
            isFullHeight
          />
          
          {/* Layout Toggle Button - Desktop only, positioned at bottom left */}
          {changeLayoutFunction && joinAgoraCall && (
            <Tooltip 
              title={agoraRtcProps?.layout === layout.grid ? "Switch to Pin Layout" : "Switch to Grid Layout"} 
              arrow
            >
              <IconButton
                onClick={changeLayoutFunction}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: 16,
                  zIndex: 10001,
                  backgroundColor: 'rgba(103, 121, 227, 0.2)',
                  backdropFilter: 'blur(12px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                  border: '1px solid rgba(103, 121, 227, 0.4)',
                  color: '#6779e3',
                  width: '40px',
                  height: '40px',
                  boxShadow: '0 4px 16px rgba(103, 121, 227, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.3)',
                    borderColor: 'rgba(103, 121, 227, 0.6)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 20px rgba(103, 121, 227, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                  '@media (max-width: 768px)': {
                    width: '36px',
                    height: '36px',
                    bottom: 80, // Position above the main controls
                    left: 16,
                  },
                }}
              >
                {agoraRtcProps?.layout === layout.grid ? (
                  <ViewComfyIcon sx={{ fontSize: 20 }} />
                ) : (
                  <ViewModuleIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          
          {/* Waiting/Support Drawer - Always open for patients in call, can be collapsed */}
          <SessionSupportDock
            renderDrawer={sessionSupportShell.showDrawer}
            drawerOpen={sessionSupportShell.showDrawer}
            showCollapsedStrip={sessionSupportShell.showCollapsedStrip}
            onCollapse={() => {
              sessionSupportShell.setIsDrawerCollapsed(true)
            }}
            onExpand={() => {
              sessionSupportShell.setIsDrawerCollapsed(false)
            }}
            sessionStartTime={bookedEventInfo?.startTime}
            mentorName={peerChatName || bookedEventInfo?.mentorName}
            mentorProfilePicId={
              bookedEventInfo?.mentorProfilePicId || bookedEventInfo?.mentorProfilePic || peerChatImage
            }
            hasMentorJoined={sessionSupportShell.hasMentorJoined}
            showOnlySupport={sessionSupportShell.showOnlySupport}
            isCollapsed={sessionSupportShell.isDrawerCollapsed}
            supportQRCodeMediaId={sessionSupportShell.supportQRCodeMediaId}
            engagementConfig={sessionSupportShell.engagementConfig}
            bookedEventId={bookedEventInfo?.id}
          />
        </>
      )}
    </Box>
  )
}

export default VideoCallContainer
